import { Box, Dialog, IconButton,Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DOMPurify from "dompurify";
import { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
interface RichEmailPreviewerProps {
  body: string;
  height?: number | string;
}

function buildIframeHtml(html: string,isDarkMode:boolean) {
  return `
    <html>
      <head>
        <style>
          body {
            margin: 0;
            padding: 8px;
          }

          img {
            display: block;
            max-width: 100% !important;
            height: auto !important;
            object-fit: contain !important;
            cursor: pointer;
          }
       ${isDarkMode
              ? `
          body, body * {
            color: #ffffff !important;
          }` : "" }
        </style>
      </head>

      <body>
        ${html}
        <script>
          document.querySelectorAll("img").forEach(function(img) {
            img.setAttribute("title", "Click to preview full image");
          });
          document.addEventListener("click", function(e) {
            const target = e.target;
            if (target && target.tagName === "IMG") {
              window.parent.postMessage(
                { type: "RICH_EMAIL_IMAGE_PREVIEW", src: target.src },
                "*"
              );
            }
          });
        </script>
      </body>
    </html>
  `;
}

export default function RichEmailPreviewer({
  body,
  height = 500,
}: RichEmailPreviewerProps) {
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
  const sanitizedHtml = useMemo(() => {
    const clean = DOMPurify.sanitize(body, {
      ADD_TAGS: ["script"],
    });
    return buildIframeHtml(clean,isDarkMode);
  }, [body,isDarkMode]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "RICH_EMAIL_IMAGE_PREVIEW") {
        setPreviewSrc(event.data.src);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const handleDownload = () => {
    if (!previewSrc) return;

    const link = document.createElement("a");
    link.href = previewSrc;
    link.download = previewSrc.split("/").pop() || "image";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Box>
        <iframe
          srcDoc={sanitizedHtml}
          width="100%"
          height={height}
          style={{ border: "none" }}
        />
      </Box>

      <Dialog
        open={Boolean(previewSrc)}
        onClose={() => setPreviewSrc(null)}
        fullScreen
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="black"
          height="100%"
          position="relative"
        >
          {/* Close */}
          <Tooltip title="Close preview" arrow>
          <IconButton
            onClick={() => setPreviewSrc(null)}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: "white",
              background: "rgba(0,0,0,0.4)",
            }}
          >
            <CloseIcon />
          </IconButton>
          </Tooltip>

          {/* Download */}
          <Tooltip title="Download image" arrow>
          <IconButton
            onClick={handleDownload}
            sx={{
              position: "absolute",
              top: 12,
              right: 64,
              color: "white",
              background: "rgba(0,0,0,0.4)",
            }}
          >
            <DownloadIcon />
          </IconButton>
          </Tooltip>

          {previewSrc && (
            <img
              src={previewSrc}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
}
